import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as authApi from '../api/auth';
import * as permissionsApi from '../api/permissions';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]         = useState(null);
  const [tenant, setTenant]     = useState(null);
  const [loading, setLoading]   = useState(true);
  const [permissions, setPermissions] = useState(null); // null = not yet loaded

  const fetchPermissions = useCallback(async () => {
    try {
      const data = await permissionsApi.getMyPermissions();
      setPermissions(data.viewKeys);
    } catch {
      setPermissions([]);
    }
  }, []);

  // canAccess: ADMIN+ always true; OPERATOR checks the permissions set
  const canAccess = useCallback((viewKey) => {
    if (!user) return false;
    if (['ADMIN', 'SUPER_ADMIN'].includes(user.role)) return true;
    if (!permissions) return false; // deny while loading (prevents flash)
    return permissions.includes(viewKey);
  }, [user, permissions]);

  const refreshPermissions = useCallback(async () => {
    await fetchPermissions();
  }, [fetchPermissions]);

  // On mount: restore session from stored token
  useEffect(() => {
    const token = localStorage.getItem('sc_access_token');
    if (!token) { setLoading(false); return; }
    authApi.getMe()
      .then(me => {
        setUser({ id: me.id, email: me.email, name: me.name, role: me.role });
        setTenant({ id: me.tenantId, name: me.tenantName, status: me.tenantStatus, trialEndsAt: me.trialEndsAt });
        return fetchPermissions();
      })
      .catch(() => {
        localStorage.removeItem('sc_access_token');
        localStorage.removeItem('sc_refresh_token');
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for forced logout (triggered by axios interceptor on failed refresh)
  useEffect(() => {
    const handler = () => { setUser(null); setTenant(null); setPermissions(null); };
    window.addEventListener('sc:logout', handler);
    return () => window.removeEventListener('sc:logout', handler);
  }, []);

  const login = useCallback(async (email, password) => {
    const data = await authApi.login(email, password);
    localStorage.setItem('sc_access_token', data.accessToken);
    localStorage.setItem('sc_refresh_token', data.refreshToken);
    setUser(data.user);
    setTenant(data.tenant);
    await fetchPermissions();
    return data;
  }, [fetchPermissions]);

  const logout = useCallback(() => {
    localStorage.removeItem('sc_access_token');
    localStorage.removeItem('sc_refresh_token');
    setUser(null);
    setTenant(null);
    setPermissions(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, tenant, loading, login, logout, permissions, canAccess, refreshPermissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
